/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useFloating,
  offset,
  flip,
  size,
  arrow
} from '@floating-ui/react-dom';

const Menu = ({
  children,
  className,
  anchorElementRef,
  matchAnchorWidth,
  onMenuPlaced,
  placement,
  sizeLimiter,
  scrollable,
  open,
  id
}) => {

  const fullHeight = useRef();
  const arrowRef = useRef();

  const {
    x: left, y: top, reference, floating, strategy, refs, placement: usedPlacement
  } = useFloating({
    placement,
    middleware: [offset(-1), flip(), size(
      {
        apply: ({ reference: { width }, floating: { height } }) => {
          const widthStyle = matchAnchorWidth ? { width: `${width}px` } : {};
          if (!fullHeight.current) {
            fullHeight.current = height;
          }
          const constrains = typeof sizeLimiter === 'function'
            ? sizeLimiter({ fullHeight: fullHeight.current })
            : {};
          Object.assign(refs.floating.current?.style, widthStyle, constrains);
        }
      }
    ), arrow({ element: arrowRef })],
  });

  useEffect(() => {
    reference(anchorElementRef.current);
  }, [anchorElementRef, reference]);

  useEffect(() => {
    if (onMenuPlaced) {
      onMenuPlaced(usedPlacement);
    }
  }, [onMenuPlaced, usedPlacement]);

  const menuClasses = classNames('sui-z-20 sui-border-1 sui-border-solid sui-border-strong sui-bg-primary',
    {
      'sui-overflow-y-auto': scrollable,
    },
    className
  );

  if (!open) return <div id={id} />;
  return (
    <div
      ref={floating}
      className={menuClasses}
      style={{
        position: strategy,
        top: top ?? '',
        left: left ?? ''
      }}
      id={id}
    >
      {children}
    </div>
  );
};

Menu.displayName = 'Menu';

Menu.propTypes = {
  /**
   * The elements to populate the Menu with.
   */
  children: PropTypes.node.isRequired,
  /**
   * @ignore
   * Additional css class names to be added.
   */
  className: PropTypes.string,
  /**
   * Event fired when the `Menu` is displayed and placed in the DOM and its final placement has been decided
   */
  onMenuPlaced: PropTypes.func,
  /**
   * If true, the `Menu` is displayed. Otherwise null.
   */
  open: PropTypes.bool,
  /**
   * A `ref` to an element that acts as an anchor point
   */
  anchorElementRef: PropTypes.shape({
    current: PropTypes.instanceOf(PropTypes.any)
  }).isRequired,
  /**
   * The location where the menu should be located relative to the `anchorElement`.
   * Uses `bottom-start` by default. See all possible options at: https://floating-ui.com/docs/tutorial#placements
   */
  placement: PropTypes.string,
  /**
   * If `true`, the Menu will match the anchor element's width.
   */
  matchAnchorWidth: PropTypes.bool,
  /**
   * The id attribute that is added to the Menu wrapper element
   */
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  /**
   * A function to restrict the size of Menu.
   * It's used, for example, to limit the height of the Menu to show a maximum of 5 items in a Dropdown
   */
  sizeLimiter: PropTypes.func,
  /**
   * If `true`, Menu will add vertical scrollbars when the content overflows
   */
  scrollable: PropTypes.bool,
};

Menu.defaultProps = {
  open: false,
  matchAnchorWidth: false,
  placement: 'bottom-start'
};

export { Menu };